import type { CurpExtractionState } from '../../services/curpExtraction.service';
import type {
	RfcPrefillReason,
	RfcPrefillResponse,
} from '../../services/rfc.service';

/** Valor de `source` del evento `rfc_prefilled`: `sat_curp` = el SAT confirmó
 * el RFC de la CURP · `taxpayer` = el RFC del taxpayer ya verificado ·
 * `calculated` = lo calculamos con la CURP, sin confirmación del SAT. */
export type RfcPrefilledSource = 'sat_curp' | 'taxpayer' | 'calculated';

/** Lo único con lo que se prellena el campo de RFC. Cualquier otro estado
 * (`pending`, `none`, error) deja el campo vacío. */
export type RfcPrefill = {
	rfc: string;
	status: 'verified' | 'unverified';
	eventSource: RfcPrefilledSource;
};

export type RfcPrefillState = {
	prefill: RfcPrefill | null;
	reason: RfcPrefillReason | null;
	curpOrigin: RfcPrefillResponse['curp_origin'];
	settled: boolean;
	pollTimedOut: boolean;
};

export type RfcPrefillMissed = {
	reason: RfcPrefillReason | 'pending_timeout' | 'user_typed_first';
	curp_origin: RfcPrefillResponse['curp_origin'];
};

/** Props `rfc_prefill_source` y `rfc_prefill_kept` de los eventos de resultado
 * de la vinculación con contraseña (`started`, `failed`, `completed`). */
export type RfcPrefillTracking = {
	rfc_prefill_source: RfcPrefilledSource | 'none';
	rfc_prefill_kept: boolean;
};

const normalizeRfc = (rfc: string | null | undefined) =>
	(rfc ?? '').replace(/\s+/g, '').toUpperCase();

/**
 * Contrato: decide con qué RFC se prellena, en este orden:
 * 1. El RFC verificado que reporta user-management-engine.
 * 2. El RFC que la extracción por CURP en curso ya validó con el SAT
 *    (`rfc_valid === true`). Cubre la CURP recién capturada en el onboarding
 *    que el servidor todavía no ve.
 * 3. El RFC calculado (sin verificar) que reporta el servidor.
 */
export const resolveRfcPrefill = (
	server: RfcPrefillResponse | null | undefined,
	extraction: CurpExtractionState | null | undefined,
): RfcPrefill | null => {
	const serverRfc = normalizeRfc(server?.rfc);
	if (server?.status === 'verified' && serverRfc) {
		return {
			rfc: serverRfc,
			status: 'verified',
			eventSource: server.source === 'taxpayer' ? 'taxpayer' : 'sat_curp',
		};
	}
	const extractionRfc = normalizeRfc(extraction?.rfc);
	if (extraction?.rfc_valid === true && extractionRfc) {
		return { rfc: extractionRfc, status: 'verified', eventSource: 'sat_curp' };
	}
	if (server?.status === 'unverified' && serverRfc) {
		return { rfc: serverRfc, status: 'unverified', eventSource: 'calculated' };
	}
	return null;
};

/** Contrato: el prellenado nunca pisa lo que el usuario escribió y ocurre una
 * sola vez, aunque el RFC llegue tarde. Si el usuario borra el RFC prellenado,
 * no se vuelve a escribir. Devuelve el RFC a escribir, o null. */
export const rfcToPrefill = (
	currentValue: string,
	prefill: RfcPrefill | null,
	alreadyPrefilled: boolean,
): string | null => {
	if (alreadyPrefilled || !prefill || currentValue) return null;
	return prefill.rfc;
};

export const buildRfcPrefillTracking = (
	prefilled: RfcPrefill | null,
	submittedRfc: string,
): RfcPrefillTracking => ({
	rfc_prefill_source: prefilled?.eventSource ?? 'none',
	rfc_prefill_kept:
		prefilled !== null &&
		normalizeRfc(prefilled.rfc) === normalizeRfc(submittedRfc),
});

/** Contrato: props de `rfc_prefill_missed`, o null si el RFC se puede
 * prellenar o si el resultado todavía no es final. */
export const resolveRfcPrefillMissed = (
	{ prefill, reason, curpOrigin, settled, pollTimedOut }: RfcPrefillState,
	fieldHasText: boolean,
): RfcPrefillMissed | null => {
	if (prefill) {
		return fieldHasText
			? { reason: 'user_typed_first', curp_origin: curpOrigin }
			: null;
	}
	if (!settled) return null;
	const missReason = pollTimedOut ? 'pending_timeout' : reason;
	return missReason ? { reason: missReason, curp_origin: curpOrigin } : null;
};
